export const RR_STEPS = [
	'start',
	'basics',
	'questions',
	'account',
	'authorize',
	'done',
] as const

export type RRStep = (typeof RR_STEPS)[number]

export function stepIndex(step: string): number {
	const i = (RR_STEPS as readonly string[]).indexOf(step)
	return i === -1 ? 0 : i
}

export function stepPath(step: RRStep): string {
	return step === 'done'
		? '/reverse-recruiter/welcome'
		: `/reverse-recruiter/${step}`
}

export const RR_TOTAL_PARTS = 4

export type IntakeState = {
	hasFile: boolean
	step: string
	expiresAt: Date | string
	claimedById: string | null
	viewerId: string | null
}

export type GateResult =
	| { allowed: true }
	| { allowed: false; redirectTo: string; reason: string }

export function gateStep(
	target: RRStep,
	intake: IntakeState | null,
	now: Date = new Date(),
): GateResult {
	if (target === 'start') return { allowed: true }

	if (!intake) {
		return {
			allowed: false,
			redirectTo: stepPath('start'),
			reason: 'no intake',
		}
	}

	const expiresAt =
		typeof intake.expiresAt === 'string'
			? new Date(intake.expiresAt)
			: intake.expiresAt
	if (expiresAt.getTime() <= now.getTime()) {
		return {
			allowed: false,
			redirectTo: stepPath('start'),
			reason: 'expired',
		}
	}

	if (intake.claimedById && intake.claimedById !== intake.viewerId) {
		return {
			allowed: false,
			redirectTo: stepPath('start'),
			reason: 'claimed by someone else',
		}
	}

	if (!intake.hasFile) {
		return {
			allowed: false,
			redirectTo: stepPath('start'),
			reason: 'no resume',
		}
	}

	const reached = stepIndex(intake.step)
	const wanted = stepIndex(target)
	if (wanted > reached + 1) {
		return {
			allowed: false,
			redirectTo: stepPath(RR_STEPS[reached] ?? 'start'),
			reason: 'too far ahead',
		}
	}

	if (target === 'authorize' && !intake.claimedById) {
		return {
			allowed: false,
			redirectTo: stepPath('account'),
			reason: 'not claimed',
		}
	}

	return { allowed: true }
}

export function furthestStep(a: string, b: RRStep): RRStep {
	return stepIndex(a) >= stepIndex(b) ? (RR_STEPS[stepIndex(a)] as RRStep) : b
}
